import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { ReactComponent as Happy } from '../../../assets/svg/GreenSmiley.svg'
import { ReactComponent as Sad } from '../../../assets/svg/RedSad.svg'
import { ReactComponent as GreenArrowUp } from '../../../assets/svg/BigArrow.svg'
import InfoButton from '../../common/InfoButton'
import { LoadingContainer } from './CheckInWidget'
import { translateTargetText, useHasStudentInPath, useQuery } from '../../../utils/util'
import { loadClassWellbeingComparison, loadStudentWellbeingComparison } from '../../../server/server'
import SelectCustomComparisonModal from './SelectCustomComparisonModal'
import { useUser } from '../../../auth'

const StudentWellbeingComparisonWidget = ({ student, daysFilter, selectedClass, isStudentAnalytics = false }) => {
    const { t } = useTranslation()
    const query = useQuery()
    const currentId = query.get('id')
    const [loading, setLoading] = useState(true)
    const [initLoading, setInitLoading] = useState(true)
    const [data, setData] = useState(null)
    const [isComparisonModalVisible, setIsComparisonModalVisible] = useState(false)
    // const isStudentAnalytics = useHasStudentInPath()
    const [firstComparison, setFirstComparison] = useState(null)
    const [secondComparison, setSecondComparison] = useState(null)
    const [callbackFunction, setCallbackFunction] = useState(() => {})
    const { user } = useUser()
    const isAdmin = user?.role == 'school_admin' || user?.role_coverage?.target_text == 'All students'

    const showLoading = useMemo(() => loading || !data, [loading, data])

    useEffect(() => {
        setLoading(true)
        loadData()
    }, [daysFilter, currentId, selectedClass, firstComparison, secondComparison])

    const loadData = async () => {
        try {
            let resp
            if (isStudentAnalytics) {
                resp = await loadStudentWellbeingComparison(currentId, daysFilter.startDate, daysFilter.endDate, selectedClass?.id ?? null)
            } else {
                const customData = {
                    firstComparison,
                    secondComparison,
                }
                resp = await loadClassWellbeingComparison(currentId, daysFilter.startDate, daysFilter.endDate, customData)
            }
            setData(resp.status === 200 ? resp.data : null)
        } catch (error) {
            console.error('Failed to load wellbeing comparison data:', error)
            setData(null)
        } finally {
            setLoading(false)
            if (initLoading) setInitLoading(false)
        }
    }

    const noStudentData = !data?.studentData?.total && !data?.classData?.total

    const showNoDataDiv = isStudentAnalytics ? !data?.studentData?.total : !data?.classData?.total

    const closeComparisonModal = (data) => {
        setIsComparisonModalVisible(false)
        if (data) callbackFunction(data)
    }

    const openComparisonModal = (order = 'first') => {
        if (order === 'first') {
            setCallbackFunction(() => (comparison) => {
                setFirstComparison(comparison)
            })
        } else {
            setCallbackFunction(() => (comparison) => {
                setSecondComparison(comparison)
            })
        }
        setIsComparisonModalVisible(true)
    }

    return (
        <div className="relative">
            <div className={`bg-white rounded-[24px] p-6 mt-4 mb-6 ${showLoading || showNoDataDiv ? 'blur' : ''}`} data-tour="wellbeing_comparison">
                <div className="flex flex-row items-center justify-between mb-6 ">
                    <div className="flex flex-row items-center gap-[8px]">
                        <p className="text-[18px] leading-[25px] font-medium text-darkenPurple ">
                            {isStudentAnalytics ? t('Student Wellbeing Comparison') : t('Class Wellbeing Comparison')}
                        </p>
                        <div className="flex items-center justify-center rounded-full bg-[#413F5E14] h-6 px-[10px] ml-2">
                            <p className="text-[13px] leading-[18px] font-normal text-strongGray ">{t(daysFilter.label)}</p>
                        </div>
                    </div>
                    <InfoButton
                        text={
                            isStudentAnalytics
                                ? t(
                                      'This section compares the student’s wellbeing data to the class and year averages for the selected period. It highlights differences in percentages, showing how the student’s performance aligns with or deviates from their peers'
                                  )
                                : t(`This data compares your class's wellbeing to the average wellbeing of the year group and the school overall`)
                        }
                    />
                </div>
                <div className="flex gap-6 w-full">
                    <div>
                        <ThreeWickChart
                            data={data}
                            student={student}
                            selectedClass={selectedClass}
                            isStudentAnalytics={isStudentAnalytics}
                            onSelectMiddleComparison={() => openComparisonModal('first')}
                            onSelectRightComparison={() => openComparisonModal('second')}
                            middleComparisonLabel={firstComparison?.label}
                            rightComparisonLabel={secondComparison?.label}
                            isCustomizable={!isStudentAnalytics && isAdmin}
                        />
                    </div>
                    {!noStudentData && (
                        <div className="flex flex-col gap-3 flex-1">
                            <ComparisonComponent
                                name={isStudentAnalytics ? `${student?.first_name} ${student?.last_name}` : t('Your class')}
                                average={
                                    isStudentAnalytics ? data?.studentData?.comfortable - data?.classData?.comfortable : data?.classData?.comfortable - data?.yearData?.comfortable
                                }
                                type={isStudentAnalytics ? 'class' : 'year'}
                                customComparisonLabel={firstComparison?.label}
                            />
                            <ComparisonComponent
                                name={isStudentAnalytics ? `${student?.first_name} ${student?.last_name}` : t('Your class')}
                                average={
                                    isStudentAnalytics ? data?.studentData?.comfortable - data?.yearData?.comfortable : data?.classData?.comfortable - data?.schoolData?.comfortable
                                }
                                type={isStudentAnalytics ? 'year' : 'school'}
                                customComparisonLabel={secondComparison?.label}
                            />
                        </div>
                    )}
                    {noStudentData && (showLoading || showNoDataDiv) && (
                        <div className="flex flex-col gap-3 flex-1">
                            <ComparisonComponent name={`Name Surname`} average={data?.studentData?.comfortable - data?.yearData?.comfortable} type={'year'} />
                            <ComparisonComponent name={`Name Surname`} average={data?.studentData?.comfortable - data?.classData?.comfortable} type={'class'} />
                        </div>
                    )}
                </div>
            </div>
            {(showLoading || showNoDataDiv) && <LoadingContainer showEmptyData={(!loading && !data) || (!loading && showNoDataDiv)} />}
            <SelectCustomComparisonModal
                showTarget={isComparisonModalVisible}
                closeTarget={closeComparisonModal}
                defaultYear={data?.defaultYear}
                currentClass={selectedClass}
                firstComparison={firstComparison}
                secondComparison={secondComparison}
            />
        </div>
    )
}

export default StudentWellbeingComparisonWidget

const ThreeWickChart = ({
    data,
    student,
    isStudentAnalytics,
    selectedClass,
    onSelectMiddleComparison = () => {},
    onSelectRightComparison = () => {},
    middleComparisonLabel,
    rightComparisonLabel,
    isCustomizable = false,
}) => {
    const { t } = useTranslation()

    return (
        <div className="flex  gap-[16px] ">
            <PercentageBar
                comfortable={isStudentAnalytics ? data?.studentData?.comfortable : data?.classData?.comfortable}
                uncomfortable={isStudentAnalytics ? data?.studentData?.uncomfortable : data?.classData?.uncomfortable}
                label={isStudentAnalytics ? `${student?.first_name} ${student?.last_name}` : `${selectedClass?.name ?? 'Class'}`}
                total={data?.studentData?.total}
                isCustomizable={isCustomizable}
            />
            <PercentageBar
                comfortable={isStudentAnalytics ? data?.classData?.comfortable : data?.yearData?.comfortable}
                uncomfortable={isStudentAnalytics ? data?.classData?.uncomfortable : data?.yearData?.uncomfortable}
                label={
                    isStudentAnalytics
                        ? t('Class average')
                        : middleComparisonLabel
                        ? t('custom average', { customLabel: translateTargetText(middleComparisonLabel) })
                        : t(`Year 3 average`, { yearName: selectedClass?.year ?? '' })
                }
                total={data?.classData?.total}
                clickable
                onClick={onSelectMiddleComparison}
                isCustomizable={isCustomizable}
            />
            <PercentageBar
                comfortable={isStudentAnalytics ? data?.yearData?.comfortable : data?.schoolData?.comfortable}
                uncomfortable={isStudentAnalytics ? data?.yearData?.uncomfortable : data?.schoolData?.uncomfortable}
                label={isStudentAnalytics ? t(`Year average`) : rightComparisonLabel ? t('custom average', { customLabel: rightComparisonLabel }) : t('School average')}
                total={data?.yearData?.total}
                clickable
                onClick={onSelectRightComparison}
                isCustomizable={isCustomizable}
            />
        </div>
    )
}

const PercentageBar = ({ comfortable = 50, uncomfortable = 50, label, total = 100, clickable = false, onClick = () => {}, isCustomizable = false }) => {
    const allZeros = comfortable === 0 && uncomfortable === 0
    const defaultHeight = allZeros ? 50 : null
    return (
        <div className="flex flex-col items-center gap-2">
            <div className="min-h-[194px] h-[194px] w-[84px] px-[2px] py-[2px] flex flex-col items-center">
                <div
                    className={`bg-[#FC4C5999] rounded-t-[20px] w-full pt-[12px] flex flex-col items-center transition-[height] duration-[0.5s] ${
                        allZeros ? '!bg-[#BEBECE66]' : ''
                    }`}
                    style={{ height: `${defaultHeight ?? uncomfortable}%` }}
                >
                    <span className="mt-0 text-[#78020B] text-16px leading-[16px] font-bold">{uncomfortable}%</span>
                </div>
                <div
                    className={`bg-[#6FCC8799] rounded-b-[20px] w-full flex pb-[12px] flex-col items-center justify-end transition-[height] duration-[0.5s]  ${
                        allZeros ? '!bg-[#BEBECE66]' : ''
                    }`}
                    style={{ height: `${defaultHeight ?? comfortable}%` }}
                >
                    <span className="mb-2 text-[#2B793F] text-16px leading-[16px] font-bold">{comfortable}%</span>
                </div>
            </div>
            {isCustomizable && clickable && (
                <button
                    className="flex flex-row items-center justify-center border border-[#ECEBF8] hover:border-darkenPurple transition rounded-full min-h-[34px] !px-3 bg-[#F8F8FB] max-w-[120px]"
                    onClick={onClick}
                >
                    <p className="break-words text-darkenPurple leading-5 text-[14px] tracking-tight first-letter:capitalize">{label}</p>
                </button>
            )}
            {(!isCustomizable || !clickable) && (
                <span className="text-[14px] w-[84px] min-h-[40px] text-center tracking-tight break-words text-darkenPurple leading-5 mb-2 font-medium first-letter:capitalize">
                    {label}
                </span>
            )}
        </div>
    )
}

const ComparisonComponent = ({ name, average: averageDifference, type, customComparisonLabel }) => {
    const { t } = useTranslation()
    const isPositive = averageDifference >= 0
    const isZero = averageDifference === 0

    const title = useMemo(() => {
        if (type === 'year') return t('comparison_to_average_year', { customLabel: customComparisonLabel ?? 'year' })
        else if (type === 'class') return t('comparison_to_average_class')
        else return t('comparison_to_average_school', { customLabel: customComparisonLabel ?? 'school' })
    }, [t, type, customComparisonLabel])

    const label = useMemo(() => {
        if (isZero) {
            if (type === 'year') return t('wellbeing_equal_average_year', { name, customLabel: customComparisonLabel ?? 'year' })
            else if (type === 'class') return t('wellbeing_equal_average_class', { name })
            else if (type === 'school') return t('wellbeing_equal_average_school', { name, customLabel: customComparisonLabel ?? 'school' })
        } else {
            if (isPositive) {
                if (type === 'year') return t('wellbeing_above_average_year', { name, percentage: Math.abs(averageDifference), customLabel: customComparisonLabel ?? 'year' })
                else if (type === 'class') return t('wellbeing_above_average_class', { name, percentage: Math.abs(averageDifference) })
                else if (type === 'school')
                    return t('wellbeing_above_average_school', { name, percentage: Math.abs(averageDifference), customLabel: customComparisonLabel ?? 'school' })
            } else {
                if (type === 'school')
                    return t('wellbeing_bellow_average_school', { name, percentage: Math.abs(averageDifference), customLabel: customComparisonLabel ?? 'school' })
                else if (type === 'year') return t('wellbeing_bellow_average_year', { name, percentage: Math.abs(averageDifference), customLabel: customComparisonLabel ?? 'year' })
                else if (type === 'class') return t('wellbeing_bellow_average_class', { name, percentage: Math.abs(averageDifference) })
            }
        }
    }, [t, isPositive, name, averageDifference, type, customComparisonLabel])

    const trendRotation = useMemo(() => {
        if (isPositive) return 'rotate-180'
        else return 'rotate-0'
    }, [isPositive, type])

    const arrowColor = useMemo(() => {
        if (isPositive) return '#6FCC87'
        else return '#FC4C59'
    }, [isPositive, type])

    return (
        <div className="flex flex-col gap-3">
            <span className="text-[16px] text-darkenPurple font-semibold leading-5 mb-2">{title}</span>
            <div
                className={`${
                    isPositive ? 'bg-[#6FCC8766]' : 'bg-[#FD9AA133]'
                } rounded-[12px] py-[9px] px-[12px] flex flex-row gap-[10px] min-h-[58px] transition-[background-color]`}
            >
                <div className="flex  w-full items-center justify-between gap-4">
                    {isPositive ? <Happy height={40} width={40} /> : <Sad height={40} width={40} />}
                    <p className="text-darkenPurple text-[14px] leading-5 font-semibold flex-1">{label}</p>
                    {!isZero && (
                        <div className="flex flex-col items-center p-[2px] gap-[2px]">
                            <div className={`${trendRotation} transition`}>
                                <GreenArrowUp color={`${arrowColor}`} />
                            </div>
                            <p className="font-semibold text-[10px] leading-[10px]">
                                {averageDifference > 0 ? '+' : ''}
                                {averageDifference}%
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
